import { MutableRefObject, useEffect, useMemo } from 'react';
import { createContext, FC, ReactNode, useReducer } from 'react';

import { MessageType } from '../../components/Message/types/messages';
import { resetChat } from './actions';
import { getInitialTranscriptContextState } from './initialTranscriptContextState';
import { createTranscriptContextReducer } from './transcriptContextReducer';
import { TranscriptContextDispatcher, TranscriptContextState } from './types';

export type TranscriptContextValue = {
  state: TranscriptContextState;
  dispatch: TranscriptContextDispatcher;

  resetSession?: () => void;
};

/**
 * Transcript Context
 */
export const TranscriptContext = createContext<TranscriptContextValue>({
  state: getInitialTranscriptContextState(),
  dispatch: () => {},
});

export type TranscriptContextProviderProps = {
  initialSessionId?: string;
  children: ReactNode;
  chatContextRef?: MutableRefObject<TranscriptContextValue | null>;
  initialMessage?: MessageType;
  placeholderMessage?: MessageType;
  onChatChange?: (state: TranscriptContextState) => void;
};

export const TranscriptContextProvider: FC<TranscriptContextProviderProps> = ({
  initialSessionId,
  initialMessage,
  placeholderMessage,
  chatContextRef,
  children,
  onChatChange,
}) => {
  const [state, dispatch] = useReducer(
    createTranscriptContextReducer(initialMessage, placeholderMessage),
    getInitialTranscriptContextState(
      initialSessionId,
      initialMessage,
      placeholderMessage,
    ),
  );

  const value = useMemo(() => {
    const resetSession = () => {
      dispatch(resetChat(''));
    };
    return {
      state,
      dispatch,
      resetSession,
    };
  }, [state]);

  if (chatContextRef) {
    chatContextRef.current = value;
  }

  useEffect(() => {
    if (onChatChange) {
      onChatChange(state);
    }
  }, [onChatChange, state]);

  return (
    <TranscriptContext.Provider value={value}>
      {children}
    </TranscriptContext.Provider>
  );
};
